import React, { useEffect, useState, useCallback } from "react"
import { useNavigate, useLocation } from "react-router-dom";
import styled from 'styled-components';
import Spinner from "../../../Plateforme/Atoms/Spinner";
import Section from "../../Atoms/Section";
import H1 from "../../Atoms/H1";
import * as api from "../../../api"
import { 
  AwardsObjectToString, 
  LiensPressesObjectToString, 
  publicToString, 
  semicolonSeparatedToCommadSeparated 
} from "../../Utils/Format";
import EnhancedTable from "../../Components/EnhancedTable";
import OwnerEditModal from "../../Components/Admin/OwnerEditModal";
import "./AnnuaireStyles.css";
import ActivityEditModal from "../../Components/Admin/ActivityEditModal";

// Styled components pour une interface améliorée
const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f5f7fa;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
`;

const PageTitle = styled(H1)`
  font-size: 1.75rem;
  color: #2d3748;
  margin: 0;
  
  span {
    font-weight: 500;
    color: #4361ee;
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 24px;
  border-bottom: none;
  padding: 0;
  overflow-x: auto;
  scrollbar-width: thin;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 1px;
    background-color: #e2e8f0;
    z-index: 0;
  }
  
  &::-webkit-scrollbar {
    height: 6px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #cbd5e0;
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a0aec0;
  }
`;

const TabItem = styled.div`
  padding: 12px 20px;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 0.2s ease;
  white-space: nowrap;
  position: relative;
  z-index: 1;
  font-weight: 500;
  color: ${props => props.active ? '#4361ee' : '#718096'};
  border-radius: 8px 8px 0 0;
  background-color: ${props => props.active ? 'white' : 'transparent'};
  box-shadow: ${props => props.active ? '0 -2px 4px rgba(0, 0, 0, 0.05)' : 'none'};
  border-bottom: ${props => props.active ? '2px solid #4361ee' : '2px solid transparent'};
  
  &:hover {
    color: #4361ee;
    background-color: ${props => props.active ? 'white' : 'rgba(67, 97, 238, 0.05)'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.4);
  }
`;

const TabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${props => props.active ? '#4361ee' : '#e9ecef'};
  color: ${props => props.active ? 'white' : '#718096'};
  border-radius: 16px;
  padding: 2px 8px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-left: 8px;
  min-width: 20px;
  height: 20px;
  transition: all 0.2s ease;
`;

const TableContainer = styled.div`
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const SpinnerContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 300px;
  width: 100%;
  background-color: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
`;

const RefreshButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  color: #4a5568;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: #f8fafc;
    border-color: #cbd5e0;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

// Icons
const RefreshIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6"></path>
    <path d="M1 20v-6h6"></path>
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"></path>
    <path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"></path>
  </svg>
);

const AnnuaireAdmin = (props) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('profiles');
    const [allProfiles, setAllProfiles] = useState(null);
    const [allCompanies, setAllCompanies] = useState(null);
    const [allProjects, setAllProjects] = useState(null);
    const [tableData, setTableData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    
    // State for managing the owner edit modal and activité edit modal
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [selectedProfileId, setSelectedProfileId] = useState(null);

    const [isOwnerEditModalOpen, setIsOwnerEditModalOpen] = useState(false);
    const [companyOwnerData, setCompanyOwnerData] = useState([]);

    const [isActivityEditModalOpen, setIsActivityEditModalOpen] = useState(false);
    const [activity, setActivity] = useState(null);

    // Function to open the owner edit modal
    const openOwnerEditModal = (companyId) => {
        setSelectedCompanyId(companyId);
        setIsOwnerEditModalOpen(true);
    };

    // Function to close the owner edit modal
    const closeOwnerEditModal = () => {
        setIsOwnerEditModalOpen(false);
        setSelectedCompanyId(null);
    };

    // Function to open the activity edit modal
    const openActivityEditModal = (companyId, profileId, currentActivity) => {
        setSelectedCompanyId(companyId);
        setSelectedProfileId(profileId);
        setActivity(currentActivity);
        setIsActivityEditModalOpen(true);
    };

    // Function to close the activity edit modal
    const closeActivityEditModal = () => {
        setIsActivityEditModalOpen(false);
        setSelectedCompanyId(null);
        setSelectedProfileId(null);
    };

    // Handle URL parameters for tab selection
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const tabParam = params.get('tab');
        const stateTab = location.state?.activeTab;
        
        if (tabParam && ['profiles', 'companies', 'projects'].includes(tabParam)) {
            setActiveTab(tabParam);
        } else if (stateTab && ['profiles', 'companies', 'projects'].includes(stateTab)) {
            setActiveTab(stateTab);
            // Update URL to reflect selected tab without page reload
            navigate(`/annuaireadmin?tab=${stateTab}`, { replace: true, state: null });
        }
    }, [location, navigate]);

    // Handle tab change and update URL
    const handleTabChange = (tab) => {
        setActiveTab(tab);
        navigate(`/annuaireadmin?tab=${tab}`, { replace: true });
    };

    // Check user authentication and admin role
    useEffect(() => {
        let isMounted = true;
        
        const checkAuth = () => {
            if(props.fullUserLoading) return;
            
            if(isMounted) {
                if(!props.fullUser?.user) navigate("/");
                if(props.fullUser?.user && props.fullUser.user.role !== "admin") navigate("/");
            }
        };
        
        checkAuth();
        
        return () => {
            isMounted = false;
        };
    }, [props.fullUserLoading, props.fullUser, navigate]);

    // Fetch all users data (used by all tabs)
    useEffect(() => {
        let isMounted = true;
        setIsLoading(true);
        
        const fetchData = async () => {
            try {
                const res = await api.GetAllUsers();

                // Only update state if component is still mounted
                if (isMounted) {
                    const _activeUsers = res.data;
                    setAllProfiles(_activeUsers);

                    // Build a Map for O(1) owner lookups instead of O(n) .find()
                    const userByIdMap = new Map();
                    for (let i = 0; i < _activeUsers.length; i++) {
                        userByIdMap.set(_activeUsers[i].user._id, _activeUsers[i]);
                    }

                    // Process companies data with Set for O(1) dedup
                    const _companies = [];
                    const seenCompanyIds = new Set();
                    for(let i = 0; i < _activeUsers.length; i++) {
                        const user = _activeUsers[i];
                        if(user.company && !seenCompanyIds.has(user.company.company._id)){
                            seenCompanyIds.add(user.company.company._id);
                            const owner = userByIdMap.get(user.company.company.ownerUserId);
                            _companies.push({...user.company, ownerEmail : owner?.user.email});
                        }
                    }
                    setAllCompanies(_companies);

                    // Process projects data with Set for O(1) dedup
                    const _projects = [];
                    const seenProjectCompanyIds = new Set();
                    for(let i = 0; i < _activeUsers.length; i++) {
                        const user = _activeUsers[i];

                        if(user.company && user.company.company?.projects?.length > 0 &&
                           !seenProjectCompanyIds.has(user.company.company._id)) {
                            seenProjectCompanyIds.add(user.company.company._id);
                            const owner = userByIdMap.get(user.company.company.ownerUserId);
                            for(let j = 0; j < user.company.company.projects.length; j++) {
                                const project = user.company.company.projects[j];
                                project.companyId = user.company.company._id;
                                if(user.company.hsCompany) {
                                    project.companyName = user.company.hsCompany.properties.name;
                                    project.ownerEmail = owner?.user.email;
                                    _projects.push(project);
                                }
                            }
                        }
                    }
                    setAllProjects(_projects);
                    setIsLoading(false);
                }
            } catch (error) {
                console.log(error);
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };
        
        fetchData();
        
        // Cleanup function to prevent state updates after unmount
        return () => {
            isMounted = false;
        };
    }, []);

    // Define the data generation functions as memoized callbacks
    const generateProfilesTableData = useCallback(() => {
        if(!allProfiles) return;
        
        const _tableData = [];
        _tableData.push([
            "Entreprise",
            "Nom",
            "Prénom",
            "Email",
            "Lien vers le profil",
            "Lien pour éditer le profil",
            "Lien vers le profil HubSpot",
            "Date de dernière connexion",
            "Date de l'inscrption à la plateforme",
            "Création profil (HubSpot)",
            "Mise à jour profil (HubSpot)",
            "Email public",
            "Activité (catégorie VIA)",
            "Actions", // Custom column for edit button
            "Évenements (programme VIA)",
            "Photo de courverture",
            "Crédit de la photo de couverture",
            "Photo de profil",
            "Crédit de la photo de profil",
            "Poste",
            "Années d'experience",
            "Ville",
            "Pays",
            "Téléphone portable",
            "Téléphone portable public",
            "Téléphone fixe",
            "Téléphone fixe public",
            "Identifiant Twitter",
            "Identifiant Instagram",
            "Lien linkedin",
            "Date de naissance",
            "Date de naissance public",
            "Nationalité",
            "Langues",
            "CV",
            "Présentation FR",
            "Présentation EN",
            "Adhérent",
            "Membre communauté",
            "Date adhésion club Le FRENCH DESIGN",
        ]);

        for(let i = 0; i < allProfiles.length; i++){
            _tableData.push([
                allProfiles[i].company?.hsCompany?.properties?.name,
                allProfiles[i].hsProfile?.properties?.lastname,
                allProfiles[i].hsProfile?.properties?.firstname,
                allProfiles[i].user.email,
                {link : true, value : "https://connectedby.lefrenchdesign.org/profile?id=" + btoa(allProfiles[i].user.email)},
                {link : true, value : "https://connectedby.lefrenchdesign.org/myprofile?id=" + btoa(allProfiles[i].user.email)},
                {link : true, value : "https://app.hubspot.com/contacts/4837156/record/0-1/" + allProfiles[i].user.hubspotID},
                allProfiles[i].user.last_connection_date,
                allProfiles[i].user.createdAt,
                allProfiles[i].hsProfile?.properties?.createdate,
                allProfiles[i].hsProfile?.properties?.lastmodifieddate,
                publicToString(allProfiles[i].user.showmail),
                semicolonSeparatedToCommadSeparated(allProfiles[i].hsProfile?.properties?.cat_gories_via),
                { 
                    button: true, // Use button property instead of link
                    label: "Éditer l'activité", // Button text
                    onClick: () => {openActivityEditModal(null, allProfiles[i].user.hubspotID, allProfiles[i].hsProfile?.properties?.cat_gories_via)} // We'll use this in a click handler
                },
                semicolonSeparatedToCommadSeparated(allProfiles[i].hsProfile?.properties?.events),
                allProfiles[i].user.cover && {link : true, value : process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/" + allProfiles[i].user.cover},
                allProfiles[i].user.creditcover,
                allProfiles[i].user.portrait && {link : true, value : process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/" + allProfiles[i].user.portrait},
                allProfiles[i].user.creditphoto,
                allProfiles[i].hsProfile?.properties?.jobtitle,
                allProfiles[i].hsProfile?.properties?.seniority,
                allProfiles[i].hsProfile?.properties?.city,
                allProfiles[i].hsProfile?.properties?.country,
                allProfiles[i].hsProfile?.properties?.mobilephone,
                publicToString(allProfiles[i].user.showmobilephone),
                allProfiles[i].hsProfile?.properties?.phone,
                publicToString(allProfiles[i].user.showphone),
                allProfiles[i].hsProfile?.properties?.twitterhandle,
                allProfiles[i].hsProfile?.properties?.instagram_account,
                allProfiles[i].hsProfile?.properties?.lien_linkedin,
                allProfiles[i].hsProfile?.properties?.date_of_birth,
                publicToString(allProfiles[i].user.showdate_of_birth),
                allProfiles[i].hsProfile?.properties?.nationalite,
                allProfiles[i].hsProfile?.properties?.langues,
                allProfiles[i].user.cv && {link : true, value : process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/" + allProfiles[i].user.cv},
                allProfiles[i].hsProfile?.properties?.presentation_libre_fr,
                allProfiles[i].hsProfile?.properties?.presentation_libre_en,
                semicolonSeparatedToCommadSeparated(allProfiles[i].hsProfile?.properties?.adherent),
                semicolonSeparatedToCommadSeparated(allProfiles[i].hsProfile?.properties?.acheteur),
                allProfiles[i].hsProfile?.properties?.date_adhesion_club_le_french_design,
            ]);
        }

        setTableData(_tableData);
    }, [allProfiles]);

    // Helper functions for companies and projects
    const ownerIdToName = useCallback((ownerId) => {
        if(!allProfiles) return "";

        const user = allProfiles.find(user => user.user._id === ownerId);
        if(!user) return "";
        return user.hsProfile?.properties?.firstname + " " + user.hsProfile?.properties?.lastname + " (" + user.user.email + ")";
    }, [allProfiles]);

    const userToNameAndEmail = useCallback((user) => {
        return user.hsProfile?.properties?.firstname + " " + user.hsProfile?.properties?.lastname + " (" + user.user.email + ")";
    }, []);

    const companyToCollaborators = useCallback((company) => {
        if(!company || !allProfiles) return [];

        const users = allProfiles
            .filter(user => user.user.companyHubspotID === company.company.hubspotID)
            .map(user => {
                return {
                    value : user.user._id,
                    label : userToNameAndEmail(user)
                }
            });
        
        return users || [];
    }, [allProfiles, userToNameAndEmail]);

    const companyToCollaboratorsString = useCallback((company) => {
        if(!company || !allProfiles) return "";

        const users = allProfiles.filter(user => user.user.companyHubspotID === company.company.hubspotID);
        if(!users || users.length === 0) return "";

        let string = "";
        for(let i = 0; i < users.length; i++){
            string += userToNameAndEmail(users[i]);
            if(i < users.length - 1) string += ", ";
        }
        return string;
    }, [allProfiles, userToNameAndEmail]);

    const companyToProjectsString = useCallback((company) => {
        if(!company || !company.company.projects) return "";

        const projects = company.company.projects;
        if(!projects || projects.length === 0) return "";

        let string = "";
        for(let i = 0; i < projects.length; i++){
            string += projects[i].name;
            if(i < projects.length - 1) string += ", ";
        }

        return string;
    }, []);

    const médiasToString = useCallback((medias) => {
        if(!medias) return "";

        let string = "";
        for(let i = 0; i < medias.length; i++){
            string += process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/" + medias[i].path;
            string += " crédit : " + medias[i].credit + "";
            string += " légende : " + medias[i].legende + "";
            if(i < medias.length - 1) string += ", ";
        }

        return string;
    }, []);
    
    // COMPANIES TAB DATA with fixed output for EnhancedTable
    const generateCompaniesTableData = useCallback(() => {
        if(!allCompanies || !allProfiles) return;
        
        // Store the company owner data separately for later use
        const companyOwners = allCompanies.map(company => ({
            companyId: company.company._id,
            ownerId: company.company.ownerUserId,
            ownerName: ownerIdToName(company.company.ownerUserId),
            collaborators: companyToCollaborators(company)
        }));
        
        const _tableData = [];
        _tableData.push([
            "Nom",
            "Responsable",
            "Actions", // Custom column for edit button
            "Email",
            "Lien vers la société",
            "Lien pour éditer la société",
            "Lien vers la société HubSpot",
            "Activité (catégorie VIA)",
            "Actions", // Custom column for edit button
            "Savoirs faire",
            "Secteur d'activité",
            "Année de création",
            "Clientèle",
            "Positionnement",
            "Adresse",
            "Code postal",
            "Ville",
            "Pays",
            "Site web",
            "Téléphone",
            "Identifiant Twitter",
            "Identifiant Instagram",
            "Lien linkedin",
            "Photo de couverture",
            "Crédit photo de couverture",
            "Logo",
            "Crédit logo",
            "Nombre de salariés",
            "Chiffre d'affaires",
            "Chiffre d'affaires public",
            "Email de l'agence RP",
            "À propos FR",
            "À propos EN",
            "Awards", 
            "Collaborateurs",
            "Projets",
        ]);

        for(let i = 0; i < allCompanies.length; i++){
            const companyId = allCompanies[i].company._id;
            
            _tableData.push([
                allCompanies[i].hsCompany?.properties?.name,
                ownerIdToName(allCompanies[i].company.ownerUserId),
                { 
                    button: true, // Use button property instead of link
                    label: "Éditer le responsable", // Button text
                    onClick: () => {openOwnerEditModal(companyId)} // We'll use this in a click handler
                },
                allCompanies[i].hsCompany?.properties?.work_email,
                {link: true, value: "https://connectedby.lefrenchdesign.org/company?id=" + allCompanies[i].company._id},
                {link: true, value: "https://connectedby.lefrenchdesign.org/mycompany?id=" + btoa(allCompanies[i].ownerEmail)},
                {link: true, value: "https://app.hubspot.com/contacts/4837156/record/0-2/" + allCompanies[i].company.hubspotID},
                semicolonSeparatedToCommadSeparated(allCompanies[i].hsCompany?.properties?.cat_gorie_via),
                { 
                    button: true, // Use button property instead of link
                    label: "Éditer l'activité", // Button text
                    onClick: () => {openActivityEditModal(allCompanies[i].company.hubspotID, null, allCompanies[i].hsCompany?.properties?.cat_gorie_via)} // We'll use this in a click handler
                },
                semicolonSeparatedToCommadSeparated(allCompanies[i].hsCompany?.properties?.savoir_faire),
                semicolonSeparatedToCommadSeparated(allCompanies[i].hsCompany?.properties?.secteur),
                allCompanies[i].hsCompany?.properties?.date_de_creation_de_l_entreprise ? allCompanies[i].hsCompany?.properties?.date_de_creation_de_l_entreprise.split("-")[0] : "",
                semicolonSeparatedToCommadSeparated(allCompanies[i].hsCompany?.properties?.clientele),
                semicolonSeparatedToCommadSeparated(allCompanies[i].hsCompany?.properties?.positionnement),
                allCompanies[i].hsCompany?.properties?.address,
                allCompanies[i].hsCompany?.properties?.zip,
                allCompanies[i].hsCompany?.properties?.city,
                allCompanies[i].hsCompany?.properties?.country,
                allCompanies[i].hsCompany?.properties?.website,
                allCompanies[i].hsCompany?.properties?.phone,
                allCompanies[i].hsCompany?.properties?.twitterhandle,
                allCompanies[i].hsCompany?.properties?.compte_instagram,
                allCompanies[i].hsCompany?.properties?.linkedin_company_page,
                allCompanies[i].company.cover_picture && {link: true, value: process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/" + allCompanies[i].company.cover_picture},
                allCompanies[i].company.coverCredit,
                allCompanies[i].company.logo && {link: true, value: process.env.REACT_APP_MEDIA_BASE_URL + "userfiles/" + allCompanies[i].company.logo},
                allCompanies[i].company.logoCredit,
                allCompanies[i].hsCompany?.properties?.numberofemployees,
                allCompanies[i].hsCompany?.properties?.annualrevenue,
                publicToString(allCompanies[i].hsCompany?.properties?.showchiffre_d_affaire_de_l_entreprise),
                allCompanies[i].hsCompany?.properties?.email_agence_rp,
                allCompanies[i].hsCompany?.properties?.description_courte_fr,
                allCompanies[i].hsCompany?.properties?.description_courte_en,
                AwardsObjectToString(allCompanies[i].hsCompany?.properties?.awards),
                companyToCollaboratorsString(allCompanies[i]),
                companyToProjectsString(allCompanies[i]),
            ]);
        }

        // Store the owner data in component state for later use
        setCompanyOwnerData(companyOwners);
        setTableData(_tableData);
    }, [
        allCompanies, 
        allProfiles, 
        ownerIdToName, 
        companyToCollaborators, 
        companyToCollaboratorsString, 
        companyToProjectsString
    ]);

    // PROJECTS TAB DATA
    const generateProjectsTableData = useCallback(() => {
        if(!allProjects) return;
        
        const _tableData = [];
        _tableData.push([
            "Société",
            "Nom",
            "Lien vers le projet",
            "Lien pour éditer le projet",
            "Projet public",
            "Année de livraison",
            "Type",
            "Typologie",
            "Médias",
            "À propos FR",
            "À propos EN",
            "Fabricant",
            "Fabrication Française",
            "Galerie",
            "Client/Commanditaire",
            "Libre à l'édition",
            "Editeur",
            "Adresse du projet",
            "Liens presse",
            "Awards",
        ]);

        for(let i = 0; i < allProjects.length; i++){
            _tableData.push([
                allProjects[i].companyName,
                allProjects[i].name,
                {link : true, value : "https://connectedby.lefrenchdesign.org/project?pid=" + allProjects[i]._id + "&cid=" + allProjects[i].companyId},
                {link : true, value : "https://connectedby.lefrenchdesign.org/myproject?id=" + allProjects[i]._id + "&as=" + btoa(allProjects[i].ownerEmail)},
                publicToString(allProjects[i].public),
                allProjects[i].year,
                allProjects[i].type,
                allProjects[i].typologie,
                médiasToString(allProjects[i].medias),
                allProjects[i].aboutfr,
                allProjects[i].abouten,
                allProjects[i].fabricant,
                allProjects[i].fabrication_fr,
                allProjects[i].galerie,
                allProjects[i].client,
                allProjects[i].libre_edition,
                allProjects[i].editeur,
                allProjects[i].adresse,
                LiensPressesObjectToString(allProjects[i].lien_presse),
                AwardsObjectToString(allProjects[i].awards),
            ]);
        }

        setTableData(_tableData);
    }, [allProjects, médiasToString]);

    // Update table data based on active tab - now using memoized callbacks
    useEffect(() => {
        if (activeTab === 'profiles' && allProfiles) {
            generateProfilesTableData();
        } else if (activeTab === 'companies' && allCompanies && allProfiles) {
            generateCompaniesTableData();
        } else if (activeTab === 'projects' && allProjects) {
            generateProjectsTableData();
        }
    }, [
        activeTab, 
        allProfiles, 
        allCompanies, 
        allProjects, 
        generateProfilesTableData, 
        generateCompaniesTableData, 
        generateProjectsTableData
    ]);

    

    // Add refreshData function
    const refreshData = useCallback(() => {
        let isMounted = true;
        setIsLoading(true);
        
        api.GetAllUsers().then(res => {
            if (!isMounted) return;

            const _activeUsers = res.data;
            setAllProfiles(_activeUsers);

            // Build a Map for O(1) owner lookups
            const userByIdMap = new Map();
            for (let i = 0; i < _activeUsers.length; i++) {
                userByIdMap.set(_activeUsers[i].user._id, _activeUsers[i]);
            }

            // Process companies data with Set for O(1) dedup
            const _companies = [];
            const seenCompanyIds = new Set();
            for(let i = 0; i < _activeUsers.length; i++) {
                const user = _activeUsers[i];
                if(user.company && !seenCompanyIds.has(user.company.company._id)){
                    seenCompanyIds.add(user.company.company._id);
                    const owner = userByIdMap.get(user.company.company.ownerUserId);
                    _companies.push({...user.company, ownerEmail : owner?.user.email});
                }
            }
            setAllCompanies(_companies);

            // Process projects data with Set for O(1) dedup
            const _projects = [];
            const seenProjectCompanyIds = new Set();
            for(let i = 0; i < _activeUsers.length; i++) {
                const user = _activeUsers[i];
                if(user.company && user.company.company?.projects?.length > 0 &&
                   !seenProjectCompanyIds.has(user.company.company._id)) {
                    seenProjectCompanyIds.add(user.company.company._id);
                    const owner = userByIdMap.get(user.company.company.ownerUserId);
                    for(let j = 0; j < user.company.company.projects.length; j++) {
                        const project = user.company.company.projects[j];
                        project.companyId = user.company.company._id;
                        if(user.company.hsCompany) {
                            project.companyName = user.company.hsCompany.properties.name;
                            project.ownerEmail = owner?.user.email;
                            _projects.push(project);
                        }
                    }
                }
            }
            setAllProjects(_projects);
            setIsLoading(false);
        }).catch(function (error) {
            if (!isMounted) return;
            console.log(error);
            setIsLoading(false);
        });
        
        return () => {
            isMounted = false;
        };
    }, [
        activeTab, 
        generateProfilesTableData,
        generateCompaniesTableData,
        generateProjectsTableData
    ]);

    // Handle button click from the table
    const handleTableButtonClick = useCallback((onClick) => {
        onClick();
    }, []);

    // Update setNewCompanyOwner function to refresh data after update
    const setNewCompanyOwner = useCallback((newOwnerId, companyId) => {
        api.SetNewCompanyOwner({newOwnerId, companyId})
            .then(() => {
                // Use modern toast notification instead of alert
                const toast = document.createElement('div');
                toast.className = 'toast-success';
                toast.textContent = 'Responsable mis à jour avec succès';
                document.body.appendChild(toast);
                
                setTimeout(() => {
                    toast.classList.add('show');
                    setTimeout(() => {
                        toast.classList.remove('show');
                        setTimeout(() => {
                            document.body.removeChild(toast);
                        }, 300);
                    }, 3000);
                }, 100);
                
                refreshData(); // Refresh data after successful update
            })
            .catch(error => {
                console.error("Error updating company owner:", error);
                
                // Use modern toast notification for errors
                const toast = document.createElement('div');
                toast.className = 'toast-error';
                toast.textContent = 'Erreur lors de la mise à jour du responsable';
                document.body.appendChild(toast);
                
                setTimeout(() => {
                    toast.classList.add('show');
                    setTimeout(() => {
                        toast.classList.remove('show');
                        setTimeout(() => {
                            document.body.removeChild(toast);
                        }, 300);
                    }, 3000);
                }, 100);
            });
    }, [refreshData]);

    const updateActivity = useCallback((companyId, profileId, activity) => {
        api.SetActivity({companyId, profileId, activity})
            .then(() => {
                // Use modern toast notification instead of alert
                const toast = document.createElement('div');
                toast.className = 'toast-success';
                toast.textContent = 'Activité mise à jour avec succès';
                document.body.appendChild(toast);
                toast.classList.add('show');

                // Properly update arrays with immutable patterns
                if (allCompanies && allCompanies.length > 0) {
                    const companyIndex = allCompanies.findIndex(company => company.company.hubspotID === companyId);
                    
                    if (companyIndex !== -1) {
                    // Create a new array with the updated company
                    const updatedCompanies = [...allCompanies];
                    
                    // Create a new company object (deep clone the one we need to modify)
                    updatedCompanies[companyIndex] = {
                        ...updatedCompanies[companyIndex],
                        hsCompany: {
                        ...updatedCompanies[companyIndex].hsCompany,
                        properties: {
                            ...updatedCompanies[companyIndex].hsCompany.properties,
                            cat_gorie_via: activity
                        }
                        }
                    };
                    
                    // Update state with the new array containing the new object reference
                    setAllCompanies(updatedCompanies);
                    }
                } 
                
                if (allProfiles && allProfiles.length > 0) {
                    const profileIndex = allProfiles.findIndex(profile => profile.user.hubspotID === profileId);
                    
                    if (profileIndex !== -1) {
                    // Create a new array with the updated profile
                    const updatedProfiles = [...allProfiles];
                    
                    // Create a new profile object (deep clone the one we need to modify)
                    updatedProfiles[profileIndex] = {
                        ...updatedProfiles[profileIndex],
                        hsProfile: {
                        ...updatedProfiles[profileIndex].hsProfile,
                        properties: {
                            ...updatedProfiles[profileIndex].hsProfile.properties,
                            cat_gories_via: activity
                        }
                        }
                    };
                    
                    // Update state with the new array containing the new object reference
                    setAllProfiles(updatedProfiles);
                    }
                }

                console.log("Activity updated successfully");
                
                setTimeout(() => {
                    toast.classList.remove('show');
                    setTimeout(() => {
                        document.body.removeChild(toast);
                    }, 500);
                }, 5000);
            }).catch(error => {
                console.error("Error updating activity:", error);
                // Use modern toast notification for errors
                const toast = document.createElement('div');
                toast.className = 'toast-error';
                toast.textContent = 'Erreur lors de la mise à jour de l\'activité';
                document.body.appendChild(toast);
                setTimeout(() => {
                    toast.classList.add('show');
                    setTimeout(() => {
                        toast.classList.remove('show');
                        setTimeout(() => {
                            document.body.removeChild(toast);
                        }, 300);
                    }, 3000);
                }, 100);
            });
    }, []);

    // Get current tab title and count
    const getTabTitle = () => {
        if (activeTab === 'profiles') {
            return `Profils`;
        } else if (activeTab === 'companies') {
            return `Sociétés`;
        } else if (activeTab === 'projects') {
            return `Projets`;
        }
        return '';
    };

    if (props.fullUserLoading || isLoading || !tableData){
        return (
            <PageContainer>
                <SpinnerContainer>
                    <Spinner/>
                </SpinnerContainer>
            </PageContainer>
        );
    }

    return (
        <PageContainer>
            <Section className="padding">
                <TabsContainer role="tablist" aria-label="Annuaire sections">
                    <TabItem 
                        active={activeTab === 'profiles'}
                        onClick={() => handleTabChange('profiles')}
                        role="tab"
                        tabIndex={activeTab === 'profiles' ? 0 : -1}
                        aria-selected={activeTab === 'profiles'}
                        aria-controls="profiles-panel"
                        id="profiles-tab"
                        onKeyDown={(e) => e.key === 'Enter' && handleTabChange('profiles')}
                    >
                        Profils <TabCount active={activeTab === 'profiles'}>{allProfiles?.length || 0}</TabCount>
                    </TabItem>
                    <TabItem 
                        active={activeTab === 'companies'}
                        onClick={() => handleTabChange('companies')}
                        role="tab"
                        tabIndex={activeTab === 'companies' ? 0 : -1}
                        aria-selected={activeTab === 'companies'}
                        aria-controls="companies-panel"
                        id="companies-tab"
                        onKeyDown={(e) => e.key === 'Enter' && handleTabChange('companies')}
                    >
                        Sociétés <TabCount active={activeTab === 'companies'}>{allCompanies?.length || 0}</TabCount>
                    </TabItem>
                    <TabItem 
                        active={activeTab === 'projects'}
                        onClick={() => handleTabChange('projects')}
                        role="tab"
                        tabIndex={activeTab === 'projects' ? 0 : -1}
                        aria-selected={activeTab === 'projects'}
                        aria-controls="projects-panel"
                        id="projects-tab"
                        onKeyDown={(e) => e.key === 'Enter' && handleTabChange('projects')}
                    >
                        Projets <TabCount active={activeTab === 'projects'}>{allProjects?.length || 0}</TabCount>
                    </TabItem>
                </TabsContainer>
            </Section>
            
            <Section className="padding">
                <PageHeader>
                    <PageTitle>{getTabTitle()} <span>({activeTab === 'profiles' ? allProfiles?.length : activeTab === 'companies' ? allCompanies?.length : allProjects?.length || 0})</span></PageTitle>
                    <RefreshButton onClick={refreshData}>
                        <RefreshIcon />
                        <span>Actualiser les données</span>
                    </RefreshButton>
                </PageHeader>
                
                <TableContainer
                    role="tabpanel"
                    id={`${activeTab}-panel`}
                    aria-labelledby={`${activeTab}-tab`}
                >
                    <EnhancedTable 
                        sortByColumnIndex={activeTab === 'profiles' ? 1 : 0} 
                        data={tableData} 
                        onButtonClick={handleTableButtonClick}
                        dataType={activeTab} // Pass the current tab as dataType for CSV naming
                    />
                </TableContainer>
                
                {/* Owner Edit Modal */}
                <OwnerEditModal
                    isOpen={isOwnerEditModalOpen}
                    onClose={closeOwnerEditModal}
                    companyId={selectedCompanyId}
                    companyOwnerData={companyOwnerData}
                    onSave={setNewCompanyOwner}
                />

                {/* Activity Edit Modal */}
                <ActivityEditModal
                    isOpen={isActivityEditModalOpen}
                    onClose={closeActivityEditModal}
                    companyId={selectedCompanyId}
                    profileId={selectedProfileId}
                    activity={activity}
                    onSave={updateActivity}
                />
                
                {/* Toast notification styles */}
                <style jsx="true">{`
                    .toast-success, .toast-error {
                        position: fixed;
                        bottom: 24px;
                        right: 24px;
                        padding: 14px 24px;
                        border-radius: 8px;
                        color: white;
                        font-weight: 500;
                        opacity: 0;
                        transform: translateY(20px);
                        transition: all 0.3s ease;
                        z-index: 1050;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                    }
                    
                    .toast-success {
                        background-color: #10b981;
                    }
                    
                    .toast-error {
                        background-color: #ef4444;
                    }
                    
                    .toast-success.show, .toast-error.show {
                        opacity: 1;
                        transform: translateY(0);
                    }
                `}</style>
            </Section>
        </PageContainer>
    );
};

export default AnnuaireAdmin;